import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  MessageSquare,
  ShieldCheck,
  Star,
  ChevronRight,
  Loader2,
  Mail,
  Phone,
  Webhook,
  Bell,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { api } from '../lib/api';
import { useBranding } from '../contexts/BrandingContext';
import { AppLayout } from '../components/AppLayout';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConsentChannel {
  channel: 'email' | 'sms' | 'push' | 'webhook';
  status: 'granted' | 'revoked' | 'pending' | 'unknown';
  capturedAt: string | null;
}

interface RecentMessage {
  id: string;
  channel: string;
  subject: string | null;
  status: string;
  requestedAt: string;
  deliveredAt: string | null;
}

interface LoyaltyInfo {
  pointsBalance: number;
  lifetimePoints: number;
  tierCode: string | null;
  programName: string | null;
}

interface DashboardProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  email: string | null;
  status: string;
  createdAt: string;
}

interface DashboardData {
  profile: DashboardProfile | null;
  consents: {
    summary: { granted: number; total: number };
    channels: ConsentChannel[];
  };
  recentMessages: RecentMessage[];
  totalMessages: number;
  loyalty: LoyaltyInfo | null;
}

// ─── Helper components ────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  subtitle,
  icon,
  linkTo,
  color = 'zinc',
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  linkTo?: string;
  color?: 'zinc' | 'primary' | 'emerald' | 'amber';
}) {
  const colorMap = {
    zinc: 'bg-zinc-50 text-zinc-500',
    primary: 'bg-[--color-primary]/10 text-[--color-primary]',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  const content = (
    <div className="bg-white border border-zinc-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorMap[color]}`}>
          {icon}
        </div>
        {linkTo && <ChevronRight size={16} className="text-zinc-400 mt-1" />}
      </div>
      <div className="mt-4">
        <p className="text-2xl font-bold text-zinc-900">{value}</p>
        <p className="text-sm font-medium text-zinc-600 mt-0.5">{title}</p>
        {subtitle && <p className="text-xs text-zinc-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }
  return content;
}

function ChannelIcon({ channel }: { channel: string }) {
  const icons: Record<string, React.ReactNode> = {
    email: <Mail size={14} />,
    sms: <Phone size={14} />,
    push: <Bell size={14} />,
    webhook: <Webhook size={14} />,
  };
  return <span className="text-zinc-400">{icons[channel] ?? <Mail size={14} />}</span>;
}

function ConsentStatusBadge({ status }: { status: string }) {
  if (status === 'granted') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
        <CheckCircle2 size={12} /> Active
      </span>
    );
  }
  if (status === 'revoked') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-500">
        <XCircle size={12} /> Revoked
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-zinc-400">
      <Clock size={12} /> Not set
    </span>
  );
}

function MessageStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    queued: 'bg-zinc-100 text-zinc-600',
    sent: 'bg-blue-50 text-blue-700',
    delivered: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-600',
    bounced: 'bg-orange-50 text-orange-600',
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${map[status] ?? 'bg-zinc-100 text-zinc-600'}`}
    >
      {status}
    </span>
  );
}

function Avatar({ profile }: { profile: DashboardProfile | null }) {
  const branding = useBranding();
  if (profile?.avatarUrl) {
    return (
      <img
        src={profile.avatarUrl}
        alt="Profile"
        className="w-14 h-14 rounded-full object-cover border-2 border-white shadow"
      />
    );
  }
  const name =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || profile?.email || 'C';
  const initials = name
    .split(' ')
    .map((w) => w[0]?.toUpperCase())
    .slice(0, 2)
    .join('');
  return (
    <div
      className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-white text-xl shadow"
      style={{ backgroundColor: branding.primaryColor }}
    >
      {initials}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<DashboardData>('/customer/dashboard')
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load dashboard. Please refresh.'))
      .finally(() => setLoading(false));
  }, []);

  const profile = data?.profile ?? null;
  const displayName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
    profile?.email ||
    'Welcome back';

  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    : null;

  return (
    <AppLayout>
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 size={24} className="animate-spin text-zinc-400" />
        </div>
      ) : error ? (
        <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-600">
          {error}
        </div>
      ) : (
        <div className="space-y-8">
          {/* ── Hero greeting ──────────────────────────────────────────────── */}
          <div className="flex items-center gap-4">
            <Avatar profile={profile} />
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">{displayName}</h1>
              {profile?.email && <p className="text-sm text-zinc-500 mt-0.5">{profile.email}</p>}
              {memberSince && (
                <p className="text-xs text-zinc-400 mt-0.5">Member since {memberSince}</p>
              )}
            </div>
          </div>

          {/* ── Stats grid ─────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Active consents"
              value={`${data?.consents?.summary?.granted ?? 0} / ${data?.consents?.summary?.total ?? 4}`}
              subtitle="Marketing channels"
              icon={<ShieldCheck size={18} />}
              linkTo="/privacy"
              color="primary"
            />
            <StatCard
              title="Messages received"
              value={data?.totalMessages ?? 0}
              subtitle="All time"
              icon={<MessageSquare size={18} />}
              linkTo="/messages"
              color="zinc"
            />
            {data?.loyalty ? (
              <StatCard
                title="Loyalty points"
                value={data.loyalty.pointsBalance.toLocaleString()}
                subtitle={data.loyalty.programName ?? undefined}
                icon={<Star size={18} />}
                linkTo="/loyalty"
                color="amber"
              />
            ) : (
              <StatCard
                title="Loyalty"
                value="—"
                subtitle="No program enrolled"
                icon={<Star size={18} />}
                color="zinc"
              />
            )}
            <StatCard
              title="Account"
              value={profile?.status === 'active' ? 'Active' : (profile?.status ?? '—')}
              subtitle="Status"
              icon={<User size={18} />}
              linkTo="/profile"
              color="emerald"
            />
          </div>

          {/* ── Consent overview ───────────────────────────────────────────── */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-zinc-900">Marketing consents</h2>
              <Link to="/privacy" className="text-sm text-[--color-primary] hover:opacity-80">
                Manage →
              </Link>
            </div>
            <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm divide-y divide-zinc-50">
              {data?.consents?.channels?.map((c) => (
                <div key={c.channel} className="flex items-center justify-between px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <ChannelIcon channel={c.channel} />
                    <span className="text-sm font-medium text-zinc-800 capitalize">
                      {c.channel}
                    </span>
                  </div>
                  <ConsentStatusBadge status={c.status} />
                </div>
              ))}
            </div>
          </section>

          {/* ── Recent messages ────────────────────────────────────────────── */}
          {data?.recentMessages && data.recentMessages.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-zinc-900">Recent messages</h2>
                <Link to="/messages" className="text-sm text-[--color-primary] hover:opacity-80">
                  View all →
                </Link>
              </div>
              <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm divide-y divide-zinc-50">
                {data.recentMessages.map((msg) => (
                  <Link
                    key={msg.id}
                    to={`/messages?id=${msg.id}`}
                    className="flex items-center justify-between px-5 py-3.5 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <ChannelIcon channel={msg.channel} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-zinc-800 truncate">
                          {msg.subject ?? `${msg.channel} message`}
                        </p>
                        <p className="text-xs text-zinc-400">
                          {new Date(msg.requestedAt).toLocaleDateString(undefined, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                    <MessageStatusBadge status={msg.status} />
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </AppLayout>
  );
}
